import React, { useState, useEffect, useRef, useCallback, Suspense } from 'react';
import ReactDOM from 'react-dom';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  LayoutDashboard, 
  Users, 
  FileText, 
  BarChart3, 
  LogOut, 
  Menu, 
  X,
  ChevronRight,
  User,
  TrendingUp,
  CalendarDays,
  Sliders,
  Search,
  Upload,
  ChevronDown,
  ListOrdered,
  Phone,
  Shield,
  Wifi,
  WifiOff,
  Signal,
  KeyRound,
  Eye,
  EyeOff,
  Check,
  AlertCircle,
  Loader2,
  Settings,
  BookOpen,
  Star,
  Layers,
  UserCog,
  ShieldCheck,
  Bell,
  Activity, Building2, ScrollText } from 'lucide-react';
import toast from 'react-hot-toast';
import { logoutUser, restoreUser } from '../store/slices/authSlice';
import { SpoofBanner, SpoofTrigger } from './SpoofUI';
import OrgScopeSwitcher from './OrgScopeSwitcher';
import useNetworkQuality from '../hooks/useNetworkQuality';
import { setCallSessionLeadId } from '../utils/callSession';
import axiosInstance from '../api/axiosInstance';
import API_ENDPOINTS from '../api/endpoints';
import { useCallStore, CALL_STATUS } from '../stores/callStore';
import DispositionModal from './DispositionModal';
import NicePhoneCallSync from './NicePhoneCallSync';
import AgentNotesWidget from './AgentNotesWidget';
import { gaPasswordChanged } from '../utils/analytics';
import { isAdmin, isManager, isTL, isAdvisor, isSupervisory, canMakeCalls, hasPermission, isTabVisible, getRoleLabel, isSuperAdmin } from '../utils/roles';
import { UsersRound } from 'lucide-react';
import styles from './Layout.module.css';

const LOGOUT_REASONS = ['Break', 'Washroom', 'Lunch', 'Meeting'];

const NAV_GROUPS = [
  { id: 'workspace', label: 'Workspace' },
  { id: 'operations', label: 'Operations' },
  { id: 'analytics', label: 'Analytics' },
  { id: 'help', label: 'Help' },
];

/** Initials for the avatar chip, e.g. "Jane Agent" → "JA" */
const initialsOf = (name) =>
  String(name || '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || '')
    .join('') || 'U';

const Layout = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);
  const { endCall } = useCallStore();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);
  const [logoutReason, setLogoutReason] = useState('');
  const [logoutSubmitting, setLogoutSubmitting] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [cpCurrent, setCpCurrent] = useState('');
  const [cpNew, setCpNew] = useState('');
  const [cpConfirm, setCpConfirm] = useState('');
  const [cpSubmitting, setCpSubmitting] = useState(false);
  const [cpError, setCpError] = useState('');
  const [cpShowCurrent, setCpShowCurrent] = useState(false);
  const [cpShowNew, setCpShowNew] = useState(false);
  const [cpForced, setCpForced] = useState(false);
  const profileRef = useRef(null);
  const { status: networkStatus, latency: networkLatency, downlink, effectiveType } = useNetworkQuality();

  const PASSWORD_RULES = [
    { label: 'At least 8 characters', test: (v) => v.length >= 8 },
    { label: 'One uppercase letter', test: (v) => /[A-Z]/.test(v) },
    { label: 'One number', test: (v) => /\d/.test(v) },
    { label: 'One special character', test: (v) => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(v) },
  ];
  const allRulesPass = PASSWORD_RULES.every((r) => r.test(cpNew));
  const passwordsMatch = cpNew && cpConfirm && cpNew === cpConfirm;

  useEffect(() => {
    if (user && !isAdmin(user) && user.passwordExpired) {
      setCpForced(true);
      setChangePasswordOpen(true);
    }
  }, [user]);

  const openChangePassword = () => {
    setProfileOpen(false);
    setMobileMenuOpen(false);
    setCpCurrent('');
    setCpNew('');
    setCpConfirm('');
    setCpError('');
    setCpShowCurrent(false);
    setCpShowNew(false);
    setChangePasswordOpen(true);
  };

  const handleChangePassword = async () => {
    if (!cpCurrent.trim()) { setCpError('Please enter your current password'); return; }
    if (!allRulesPass) { setCpError('New password does not meet all requirements'); return; }
    if (cpNew !== cpConfirm) { setCpError('Passwords do not match'); return; }

    setCpSubmitting(true);
    setCpError('');
    try {
      const res = await axiosInstance.put(API_ENDPOINTS.changePassword, {
        currentPassword: cpCurrent,
        newPassword: cpNew,
      });
      const d = res.data?.response?.[0];
      if (d?.status === 1) {
        toast.success('Password changed successfully');
        gaPasswordChanged(user?.userCode);
        const updatedUser = { ...user, passwordExpired: false };
        dispatch(restoreUser(updatedUser));
        localStorage.setItem('user', JSON.stringify(updatedUser));
        setChangePasswordOpen(false);
        setCpForced(false);
      } else {
        setCpError(d?.message || 'Failed to change password');
      }
    } catch (err) {
      setCpError(err.response?.data?.response?.[0]?.message || 'Failed to change password');
    } finally {
      setCpSubmitting(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) setProfileOpen(false);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const openLogoutModal = () => {
    setProfileOpen(false);
    setMobileMenuOpen(false);
    setLogoutModalOpen(true);
    setLogoutReason('');
  };

  const clearAllLocalStorage = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('nicedial-call-storage');
    localStorage.removeItem('nicedial-autodial');
    localStorage.removeItem('auth');
  };

  const handleLogoutConfirm = async () => {
    if (!LOGOUT_REASONS.includes(logoutReason)) return;
    setLogoutSubmitting(true);
    try {
      // NicePhone is the only webphone; the Ameyo branch that used to sit here is gone.
      {
        if (typeof window.CRMCallHeaderSecure !== 'undefined' && window.CRMCallHeaderSecure.hangup) {
          try {
            window.CRMCallHeaderSecure.hangup();
          } catch (e) {
            console.warn('NicePhone hangup error:', e);
          }
        }
      }
      
      // Dispatch logout action
      try {
        await dispatch(logoutUser({ userCode: user?.userCode, logoutReason })).unwrap();
      } catch (err) {
        console.warn('Logout API error (continuing anyway):', err);
      }
      
      // Clear all local storage
      clearAllLocalStorage();
      
      // Clear Redux state using the logout action
      dispatch({ type: 'auth/logout' });
      
      // Force navigation to login
      window.location.href = '/login';
    } catch (error) {
      console.error('Logout error:', error);
      // Even on error, clear state and navigate
      clearAllLocalStorage();
      dispatch({ type: 'auth/logout' });
      navigate('/login', { replace: true });
    } finally {
      setLogoutModalOpen(false);
      setLogoutSubmitting(false);
    }
  };

  const showCrmHandler = useCallback((phone) => {
    if (!phone) return;
    
    const currentLead = useCallStore.getState().currentLead;
    if (currentLead) {
      console.log('showCrm: Already have current lead, not fetching by phone', {
        currentLeadId: currentLead.leadID,
        phone
      });
      setCallSessionLeadId(currentLead.leadID || currentLead.id);
      if (window.location.pathname !== '/my-leads') {
        navigate('/my-leads');
      }
      return;
    }
    
    const normalizedPhone = String(phone).replace(/\D/g, '').slice(-10);
    if (!normalizedPhone) return;
    
    console.log('showCrm: Fetching lead by phone (no current lead)', { phone: normalizedPhone });
    
    axiosInstance
      .get(API_ENDPOINTS.getLeadData, { params: { leadMobileNumber: normalizedPhone, includeDetails : true } })
      .then((res) => {
        const data = res.data?.response?.[0]?.data;
        const lead = Array.isArray(data) ? data[0] : data;
        if (lead) {
          const resolvedLeadId = lead.leadID || lead.id;
          setCallSessionLeadId(resolvedLeadId);
          useCallStore.getState().setCurrentLead(lead);
          navigate('/my-leads');
        } else {
          navigate('/my-leads', { state: { incomingPhone: phone } });
        }
      })
      .catch(() => {
        navigate('/my-leads', { state: { incomingPhone: phone } });
      });
  }, [navigate]);

  // Build nav items based on user role and tabVisibility permissions
  const filteredNavItems = (() => {
    const items = [];

    if (isTabVisible(user, 'leads')) {
      items.push({ path: '/leads', icon: FileText, label: 'Leads', group: 'workspace' });
    }

      if (isTabVisible(user, 'users')) {
        items.push({ path: '/users', icon: Users, label: 'Users', group: 'workspace' });
      }

      // Platform-level, superadmin only — not an org admin capability, and not
      // governed by the per-org feature switches it manages.
      if (isSuperAdmin(user)) {
        items.push({ path: '/organisations', icon: Building2, label: 'Organisations', short: 'Orgs', group: 'workspace' });
        items.push({ path: '/api-keys', icon: KeyRound, label: 'API keys', short: 'Keys', group: 'workspace' });
        items.push({ path: '/server-health', icon: Activity, label: 'Server health', short: 'Health', group: 'workspace' });
        items.push({ path: '/api-logs', icon: ScrollText, label: 'API logs', short: 'Logs', group: 'workspace' });
      }

    if (isTabVisible(user, 'team')) {
      items.push({ path: '/team', icon: UsersRound, label: 'My Team', short: 'Team', group: 'workspace' });
    }

    if (isTabVisible(user, 'queueManagement')) {
      items.push({ path: '/queue-management', icon: ListOrdered, label: 'Queue Management', short: 'Queues', group: 'workspace' });
    }

    if (isTabVisible(user, 'bulkUpload')) {
      items.push({ path: '/file-upload', icon: Upload, label: 'Bulk Upload', short: 'Upload', group: 'operations' });
    }

    if (isTabVisible(user, 'lms')) {
      items.push({ path: '/lms', icon: Layers, label: 'LMS', group: 'operations' });
    }

    if (isTabVisible(user, 'ums')) {
      items.push({ path: '/ums', icon: UserCog, label: 'UMS', group: 'operations' });
    }

    if (isTabVisible(user, 'priorityConfig')) {
      items.push({ path: '/priority-config', icon: Sliders, label: 'Priority Config', group: 'operations' });
    }

    if (isTabVisible(user, 'webphoneConfig')) {
      items.push({ path: '/webphone-config', icon: Phone, label: 'Webphone Config', group: 'operations' });
    }

    if (isTabVisible(user, 'ipManagement')) {
      items.push({ path: '/ip-management', icon: ShieldCheck, label: 'IP Management', group: 'operations' });
    }

    if (isTabVisible(user, 'reports')) {
      items.push({ path: '/reports', icon: BarChart3, label: 'Reports', group: 'analytics' });
    }

    // Call Ratings tab intentionally hidden on frontend navigation
    if (isTabVisible(user, 'callRatings')) {
      items.push({ path: '/call-ratings', icon: Star, label: 'Call Ratings', group: 'analytics' });
    }
    if (isTabVisible(user, 'rtm')) {
      items.push({ path: '/rtm', icon: Activity, label: 'Real-Time Monitor', short: 'Live' });
    }

    // Advisor-only pages
    if (isAdvisor(user)) {
      items.push({ path: '/my-leads', icon: CalendarDays, label: 'My Leads', short: 'Leads', group: 'workspace' });
      items.push({ path: '/my-performance', icon: TrendingUp, label: 'My Performance', short: 'Stats', group: 'workspace' });
      items.push({ path: '/my-summary', icon: Search, label: 'My Summary', short: 'Search', group: 'workspace' });
    }

    if (isTabVisible(user, 'guide')) {
      items.push({ path: '/guide', icon: BookOpen, label: 'Guide', group: 'help' });
    }

    return items;
  })();

  // Header title follows the current section (longest matching nav path wins)
  const pageTitle = (() => {
    const path = location.pathname;
    if (path.startsWith('/leads/') || path.startsWith('/lead-detail/')) return 'Lead Details';
    const match = [...filteredNavItems]
      .sort((a, b) => b.path.length - a.path.length)
      .find((item) => path === item.path || path.startsWith(`${item.path}/`));
    return match?.label || 'Dashboard';
  })();

  return (
    <div className={styles.layout}>
      {/* Desktop Sidebar */}
      <aside className={`${styles.sidebar} ${sidebarOpen ? styles.sidebarOpen : styles.sidebarClosed}`}>
        <div className={styles.sidebarHeader}>
          <div className={styles.logo}>
            <div className={styles.logoIcon} aria-label="Relabix">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M6 4h7.5a5.5 5.5 0 0 1 1.9 10.66L19.5 20H15l-3.6-4.8H10V20H6V4Zm4 3.6v4h3.4a2 2 0 1 0 0-4H10Z" fill="currentColor" />
              </svg>
            </div>
            {sidebarOpen && (
              <motion.span 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className={styles.logoText}
              >
                Relabix
              </motion.span>
            )}
          </div>
          <button 
            className={styles.toggleButton}
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            <ChevronRight className={`${styles.toggleIcon} ${!sidebarOpen ? styles.rotated : ''}`} size={20} />
          </button>
        </div>

        <div className={styles.networkStrip} data-status={networkStatus}>
          <div className={styles.networkStripLeft}>
            {networkStatus === 'offline' ? <WifiOff size={14} /> : <Wifi size={14} />}
            {sidebarOpen && (
              <span className={styles.networkStatusLabel}>
                {networkStatus.charAt(0).toUpperCase() + networkStatus.slice(1)}
              </span>
            )}
          </div>
          {sidebarOpen && (
            <div className={styles.networkStripRight}>
              {/* {networkLatency != null && (
                <span className={styles.networkStat}>
                  <Signal size={12} />
                  {networkLatency}ms
                </span>
              )} */}
              {downlink != null && networkStatus !== 'offline' && (
                <span className={styles.networkStat}>
                  {downlink >= 1 ? `${downlink} Mbps` : `${Math.round(downlink * 1000)} Kbps`}
                </span>
              )}
              {effectiveType && networkStatus !== 'offline' && (
                <span className={styles.networkEffType}>{effectiveType.toUpperCase()}</span>
              )}
            </div>
          )}
        </div>

        <nav className={styles.nav}>
          {NAV_GROUPS.map((group) => {
            const groupItems = filteredNavItems.filter((i) => (i.group || 'workspace') === group.id);
            if (groupItems.length === 0) return null;
            return (
              <div key={group.id} className={styles.navGroup}>
                <div className={styles.navGroupLabel} aria-hidden={!sidebarOpen}>{group.label}</div>
                {groupItems.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    title={sidebarOpen ? undefined : item.label}
                    className={({ isActive }) =>
                      `${styles.navItem} ${isActive ? styles.navItemActive : ''}`
                    }
                  >
                    <item.icon size={18} className={styles.navIcon} />
                    {sidebarOpen && <span className={styles.navLabel}>{item.label}</span>}
                  </NavLink>
                ))}
              </div>
            );
          })}
        </nav>

        <div className={styles.sidebarFooter}>
          {sidebarOpen ? (user?.userOrgName || 'Relabix CRM') : 'R'}
        </div>
      </aside>

      {/* Mobile Bottom Navigation */}
      <nav className={styles.mobileNav}>
        {filteredNavItems.slice(0, 4).map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            className={({ isActive }) => 
              `${styles.mobileNavItem} ${isActive ? styles.mobileNavItemActive : ''}`
            }
          >
            <item.icon size={20} />
            <span>{item.short || item.label.split(' ')[0]}</span>
          </NavLink>
        ))}
        <button 
          className={styles.mobileNavItem}
          onClick={() => setMobileMenuOpen(true)}
        >
          <Menu size={20} />
          <span>More</span>
        </button>
      </nav>

      {/* Main Content */}
      <main className={styles.main}>
        {/* Top Header */}
        <header className={styles.header}>
          <div className={styles.headerLeft}>
            <button 
              className={styles.mobileMenuButton}
              onClick={() => setMobileMenuOpen(true)}
            >
              <Menu size={24} />
            </button>
            <h1 className={styles.pageTitle}>{pageTitle}</h1>
          </div>

          <div className={styles.headerRight} ref={profileRef}>
            <OrgScopeSwitcher />
            <SpoofTrigger />
            <button
              type="button"
              className={styles.profileTrigger}
              onClick={() => setProfileOpen((o) => !o)}
              aria-expanded={profileOpen}
              aria-haspopup="true"
            >
              <div className={styles.userAvatar} aria-hidden="true">
                {initialsOf(user?.userName)}
              </div>
              <div className={styles.userDetails}>
                <span className={styles.userName}>{user?.userName}</span>
                <span className={styles.userRole}>
                  {getRoleLabel(user)}
                </span>
              </div>
              <ChevronDown size={18} className={styles.profileChevron} />
            </button>
            <AnimatePresence>
              {profileOpen && (
                <motion.div
                  className={styles.profileDropdown}
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.15 }}
                >
                  {!isAdmin(user) && (
                    <button
                      type="button"
                      className={styles.profileDropdownItem}
                      onClick={openChangePassword}
                    >
                      <KeyRound size={18} />
                      <span>Change Password</span>
                    </button>
                  )}
                  <button
                    type="button"
                    className={styles.profileDropdownItem}
                    onClick={openLogoutModal}
                  >
                    <LogOut size={18} />
                    <span>Sign Out</span>
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </header>

        {/* Logout reason modal */}
        {ReactDOM.createPortal(
          <AnimatePresence>
            {logoutModalOpen && (
              <div className={styles.logoutModalContainer}>
                <motion.div
                  className={styles.logoutOverlay}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={() => !logoutSubmitting && setLogoutModalOpen(false)}
                />
                <motion.div
                  className={styles.logoutModal}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <h3 className={styles.logoutModalTitle}>Reason for logout</h3>
                  <p className={styles.logoutModalHint}>Please select a reason before signing out.</p>
                  <div className={styles.logoutReasons}>
                    {LOGOUT_REASONS.map((reason) => (
                      <label key={reason} className={styles.logoutReasonLabel}>
                        <input
                          type="radio"
                          name="logoutReason"
                          value={reason}
                          checked={logoutReason === reason}
                          onChange={() => setLogoutReason(reason)}
                        />
                        <span>{reason}</span>
                      </label>
                    ))}
                  </div>
                  <div className={styles.logoutModalActions}>
                    <button
                      type="button"
                      className={styles.logoutCancelBtn}
                      onClick={() => setLogoutModalOpen(false)}
                      disabled={logoutSubmitting}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className={styles.logoutConfirmBtn}
                      onClick={handleLogoutConfirm}
                      disabled={!logoutReason || logoutSubmitting}
                    >
                      {logoutSubmitting ? 'Signing out...' : 'Sign Out'}
                    </button>
                  </div>
                </motion.div>
              </div>
            )}
          </AnimatePresence>,
          document.body
        )}

        {/* Change Password modal */}
        {ReactDOM.createPortal(
          <AnimatePresence>
            {changePasswordOpen && (
              <div className={styles.logoutModalContainer}>
                <motion.div
                  className={styles.logoutOverlay}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={() => !cpForced && !cpSubmitting && setChangePasswordOpen(false)}
                />
                <motion.div
                  className={styles.logoutModal}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  onClick={(e) => e.stopPropagation()}
                  style={{ maxWidth: 420 }}
                >
                  <h3 className={styles.logoutModalTitle}>
                    <KeyRound size={20} />
                    {cpForced ? 'Password Expired' : 'Change Password'}
                  </h3>
                  {cpForced && (
                    <p className={styles.logoutModalHint} style={{ color: 'var(--error)' }}>
                      Your password has expired. Please set a new password to continue.
                    </p>
                  )}

                  {cpError && (
                    <div className={styles.cpError}>
                      <AlertCircle size={14} />
                      <span>{cpError}</span>
                    </div>
                  )}

                  <div className={styles.cpField}>
                    <label>Current Password</label>
                    <div className={styles.cpInputWrap}>
                      <input
                        type={cpShowCurrent ? 'text' : 'password'}
                        value={cpCurrent}
                        onChange={(e) => setCpCurrent(e.target.value)}
                        placeholder="Enter current password"
                        disabled={cpSubmitting}
                      />
                      <button type="button" className={styles.cpEye} onClick={() => setCpShowCurrent((v) => !v)}>
                        {cpShowCurrent ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>

                  <div className={styles.cpField}>
                    <label>New Password</label>
                    <div className={styles.cpInputWrap}>
                      <input
                        type={cpShowNew ? 'text' : 'password'}
                        value={cpNew}
                        onChange={(e) => { setCpNew(e.target.value); setCpError(''); }}
                        placeholder="Enter new password"
                        disabled={cpSubmitting}
                      />
                      <button type="button" className={styles.cpEye} onClick={() => setCpShowNew((v) => !v)}>
                        {cpShowNew ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                    <ul className={styles.cpRules}>
                      {PASSWORD_RULES.map((rule) => (
                        <li key={rule.label} className={rule.test(cpNew) ? styles.cpRulePass : ''}>
                          <Check size={12} />
                          <span>{rule.label}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className={styles.cpField}>
                    <label>Confirm Password</label>
                    <input
                      type="password"
                      value={cpConfirm}
                      onChange={(e) => { setCpConfirm(e.target.value); setCpError(''); }}
                      placeholder="Re-enter new password"
                      disabled={cpSubmitting}
                      className={cpConfirm && !passwordsMatch ? styles.cpInputError : ''}
                    />
                    {cpConfirm && !passwordsMatch && (
                      <span className={styles.cpMismatch}>Passwords do not match</span>
                    )}
                  </div>

                  <div className={styles.logoutModalActions}>
                    {!cpForced && (
                      <button
                        type="button"
                        className={styles.logoutCancelBtn}
                        onClick={() => setChangePasswordOpen(false)}
                        disabled={cpSubmitting}
                      >
                        Cancel
                      </button>
                    )}
                    <button
                      type="button"
                      className={styles.logoutConfirmBtn}
                      onClick={handleChangePassword}
                      disabled={cpSubmitting || !cpCurrent || !allRulesPass || !passwordsMatch}
                    >
                      {cpSubmitting ? (
                        <><Loader2 size={16} className={styles.spinning} /> Changing...</>
                      ) : 'Change Password'}
                    </button>
                  </div>
                </motion.div>
              </div>
            )}
          </AnimatePresence>,
          document.body
        )}

        {/* Spoof banner - when superadmin is viewing as another user */}
        <SpoofBanner />

        {/* Page Content — Suspense keeps the sidebar/header on screen while the
            lazily-loaded page chunk arrives (see the code-split imports in App.js). */}
        <div className={styles.content}>
          <Suspense
            fallback={
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '4rem', color: '#64748b' }}>
                <Loader2 size={22} className={styles.spinning} />
              </div>
            }
          >
            <Outlet />
          </Suspense>
        </div>
      </main>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className={styles.overlay}
              onClick={() => setMobileMenuOpen(false)}
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className={styles.mobileMenu}
            >
              <div className={styles.mobileMenuHeader}>
                <div className={styles.mobileUserInfo}>
                  <div className={styles.mobileUserAvatar} aria-hidden="true">
                    {initialsOf(user?.userName)}
                  </div>
                  <div>
                    <p className={styles.mobileUserName}>{user?.userName}</p>
                    <p className={styles.mobileUserRole}>{user?.userCode}</p>
                  </div>
                </div>
                <button 
                  className={styles.closeButton}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <X size={24} />
                </button>
              </div>

              <nav className={styles.mobileMenuNav}>
                {NAV_GROUPS.map((group) => {
                  const groupItems = filteredNavItems.filter((i) => (i.group || 'workspace') === group.id);
                  if (groupItems.length === 0) return null;
                  return (
                    <div key={group.id} className={styles.navGroup}>
                      <div className={styles.navGroupLabel}>{group.label}</div>
                      {groupItems.map((item) => (
                        <NavLink
                          key={item.path}
                          to={item.path}
                          className={({ isActive }) => 
                            `${styles.mobileMenuItem} ${isActive ? styles.mobileMenuItemActive : ''}`
                          }
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          <item.icon size={20} />
                          <span>{item.label}</span>
                        </NavLink>
                      ))}
                    </div>
                  );
                })}
              </nav>

              <div className={styles.mobileMenuFooter}>
                <button 
                  type="button"
                  className={styles.mobileLogoutButton}
                  onClick={openLogoutModal}
                >
                  <LogOut size={22} />
                  <span>Sign Out</span>
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* NicePhone call lifecycle (SSE / SIP events / server reconcile) — global so a
          call that ends on another page still opens the disposition modal */}
      <NicePhoneCallSync />

      {/* Disposition modal - opens on call hangup, available from any page */}
      <DispositionModal />

      {/* Agent Notes — global floating widget for advisors and TLs with call access */}
      {user && !isAdmin(user) && !isManager(user) && <AgentNotesWidget />}
    </div>
  );
};

export default Layout;
